import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Check, Clock, MapPin, Users, DollarSign, MessageCircle } from "lucide-react";
import { createApiRequest } from "@/utils/apiUtils";
import { formatDate } from "@/utils/dateUtils";
import { formatPrice, formatDuration } from "@/utils/tripFormatUtils";
import { useToast } from "@/hooks/use-toast";

const OperatorTripList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [trips, setTrips] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all trips for the operator
  useEffect(() => {
    const fetchTrips = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log("Fetching all trips for operator with correct token");
        // Use type=claimed to filter server-side if that's what the API expects
        const request = createApiRequest("trips/?type=claimed");
        const response = await fetch(request);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch trips");
        }
        
        const data = await response.json();
        console.log("✅ Trips fetched successfully:", data);
        
        // Set trips from the results
        setTrips(data.results || []);
      } catch (error) {
        console.error("Error fetching trips:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to fetch trips";
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrips();
  }, [toast]);

  // Filter trips based on search and filters
  const filteredTrips = trips.filter(trip => {
    const matchesSearch = searchQuery === "" || 
      trip.community_event?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trip.to_address?.formatted?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trip.from_address?.formatted?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trip.id.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCity = cityFilter === "all" || 
      trip.to_address?.locality?.name === cityFilter;

    const matchesStatus = statusFilter === "all" || 
      getStatusFromTripState(trip.trip_state) === statusFilter;

    return matchesSearch && matchesCity && matchesStatus;
  });

  // Get unique cities for filter dropdown
  const cities = Array.from(new Set(
    trips
      .map(trip => trip.to_address?.locality?.name)
      .filter(Boolean)
  ));

  // Convert trip_state to readable status
  const getStatusFromTripState = (tripState: string) => {
    switch (tripState) {
      case 'UNCLAIMED': return 'unclaimed';
      case 'BOOKED': return 'booked';
      case 'CLAIMED': return 'claimed';
      case 'DRIVER_ASSIGNED': return 'driver_assigned';
      case 'DRIVER_CONFIRMED': return 'driver_confirmed';
      case 'COMPLETED': return 'completed';
      case 'ABANDONED': return 'abandoned';
      case 'CANCELED': return 'canceled';
      default: return 'unclaimed';
    }
  };

  const getStatusBadge = (tripState: string) => {
    const status = getStatusFromTripState(tripState);
    
    switch (status) {
      case "unclaimed":
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Unclaimed</Badge>;
      case "booked":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Booked (unclaimed, Payment held/charged)</Badge>;
      case "claimed":
        return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Claimed</Badge>;
      case "driver_assigned":
        return <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100">Driver Assigned, Payment Captured</Badge>;
      case "driver_confirmed":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Driver "Confirmed Day Of"</Badge>;
      case "completed":
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Completed</Badge>;
      case "abandoned":
        return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Abandoned (Canceled prior to driver being assigned)</Badge>;
      case "canceled":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Canceled (Canceled after driver assigned, payment refunded)</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Unknown</Badge>;
    }
  };

  const handleManageTrip = (tripId: string) => {
    // Navigate to driver-trip-sync with the trip ID as a query parameter
    navigate(`/driver-trip-sync?tripId=${tripId}&autoStart=true`);
  };

  const getUrgencyLevel = (pickupDate: string) => {
    const now = new Date();
    const tripDate = new Date(pickupDate);
    const diffTime = tripDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { level: 'Past', className: 'bg-gray-100 text-gray-800 hover:bg-gray-100' };
    } else if (diffDays < 3) {
      return { level: 'High', className: 'bg-red-100 text-red-800 hover:bg-red-100' };
    } else if (diffDays < 7) {
      return { level: 'Medium', className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' };
    } else if (diffDays < 60) {
      return { level: 'Low', className: 'bg-green-100 text-green-800 hover:bg-green-100' };
    } else {
      return { level: 'Low', className: 'bg-green-100 text-green-800 hover:bg-green-100' };
    }
  };

  const getProgressIcons = (tripState: string) => {
    const status = getStatusFromTripState(tripState);
    const iconClass = "w-6 h-6 rounded-full flex items-center justify-center text-white text-xs";
    
    if (status === "completed") {
      return (
        <div className="flex items-center gap-1">
          <div className={`${iconClass} bg-purple-600`}>
            <Check className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <Clock className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <MessageCircle className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <MapPin className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <MessageCircle className="w-3 h-3" />
          </div>
        </div>
      );
    } else if (status === "driver_confirmed") {
      return (
        <div className="flex items-center gap-1">
          <div className={`${iconClass} bg-purple-600`}>
            <Check className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <Clock className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <MessageCircle className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <MapPin className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-gray-300"></div>
          <div className={`${iconClass} bg-gray-300`}>
            <MessageCircle className="w-3 h-3" />
          </div>
        </div>
      );
    } else if (status === "driver_assigned") {
      return (
        <div className="flex items-center gap-1">
          <div className={`${iconClass} bg-purple-600`}>
            <Check className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <Clock className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <MessageCircle className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-gray-300"></div>
          <div className={`${iconClass} bg-gray-300`}>
            <MapPin className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-gray-300"></div>
          <div className={`${iconClass} bg-gray-300`}>
            <MessageCircle className="w-3 h-3" />
          </div>
        </div>
      );
    } else if (status === "claimed") {
      return (
        <div className="flex items-center gap-1">
          <div className={`${iconClass} bg-purple-600`}>
            <Check className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-purple-600"></div>
          <div className={`${iconClass} bg-purple-600`}>
            <Clock className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-gray-300"></div>
          <div className={`${iconClass} bg-gray-300`}>
            <MessageCircle className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-gray-300"></div>
          <div className={`${iconClass} bg-gray-300`}>
            <MapPin className="w-3 h-3" />
          </div>
          <div className="w-3 h-0.5 bg-gray-300"></div>
          <div className={`${iconClass} bg-gray-300`}>
            <MessageCircle className="w-3 h-3" />
          </div>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-1">
        <div className={`${iconClass} bg-gray-300`}>
          <Check className="w-3 h-3" />
        </div>
        <div className="w-3 h-0.5 bg-gray-300"></div>
        <div className={`${iconClass} bg-gray-300`}>
          <Clock className="w-3 h-3" />
        </div>
        <div className="w-3 h-0.5 bg-gray-300"></div>
        <div className={`${iconClass} bg-gray-300`}>
          <MessageCircle className="w-3 h-3" />
        </div>
        <div className="w-3 h-0.5 bg-gray-300"></div>
        <div className={`${iconClass} bg-gray-300`}>
          <MapPin className="w-3 h-3" />
        </div>
        <div className="w-3 h-0.5 bg-gray-300"></div>
        <div className={`${iconClass} bg-gray-300`}>
          <MessageCircle className="w-3 h-3" />
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">Loading trips...</div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-red-600">Error loading trips: {error}</div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-black">My Trips</h1>
          </div>
          <Button 
            onClick={() => navigate("/driver-trip-sync")}
            className="px-6 py-2 text-sm font-semibold text-black rounded-lg gradient-bg hover:opacity-90 transition-opacity"
          >
            Manage Trip
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black w-4 h-4" />
            <Input
              placeholder="Search by destination, operator, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white text-black border-gray-300 placeholder:text-gray-500"
            />
          </div>
          <div className="flex gap-2">
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-40 bg-white text-black border-gray-300">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city} value={city!}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white text-black border-gray-300">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="unclaimed">Unclaimed</SelectItem>
                <SelectItem value="booked">Booked</SelectItem>
                <SelectItem value="claimed">Claimed</SelectItem>
                <SelectItem value="driver_assigned">Driver Assigned</SelectItem>
                <SelectItem value="driver_confirmed">Driver Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="abandoned">Abandoned</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Trips Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-black font-semibold w-20">Action</TableHead>
                <TableHead className="text-black font-semibold">Trip ID</TableHead>
                <TableHead className="text-black font-semibold">Date</TableHead>
                <TableHead className="text-black font-semibold">Pickup/Drop Off</TableHead>
                <TableHead className="text-black font-semibold">Destination</TableHead>
                <TableHead className="text-black font-semibold">Duration</TableHead>
                <TableHead className="text-black font-semibold">City</TableHead>
                <TableHead className="text-black font-semibold">Price</TableHead>
                <TableHead className="text-black font-semibold">Capacity</TableHead>
                <TableHead className="text-black font-semibold">Rider Contact</TableHead>
                <TableHead className="text-black font-semibold">Urgency</TableHead>
                <TableHead className="text-black font-semibold">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrips.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={12} className="text-center py-8 text-gray-500">
                    No trips found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredTrips.map((trip) => (
                  <TableRow key={trip.id} className="hover:bg-transparent">
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => handleManageTrip(trip.id)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs rounded"
                      >
                        Manage
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium text-black">
                      {trip.id}
                    </TableCell>
                    <TableCell className="font-medium text-black">
                      {formatDate(trip.pickup_at)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-black">
                        <div>{new Date(trip.pickup_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                        {trip.is_round_trip && (
                          <div className="text-gray-500">Round trip</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-black">
                        {trip.community_event?.name || trip.to_address?.formatted || "Unknown"}
                      </div>
                    </TableCell>
                    <TableCell className="text-black">
                      {formatDuration(trip.booking_duration)}
                    </TableCell>
                    <TableCell className="text-black">
                      {trip.to_address?.locality?.name || "N/A"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-black">
                        <DollarSign className="w-4 h-4" />
                        {formatPrice((trip as any).rider_price || trip.organization_price) || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-black">
                        <Users className="w-4 h-4" />
                        {trip.passenger_count || "N/A"} people
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {trip.rider_mobile_number ? (
                          <div>
                            <div className="text-black">Contact Available</div>
                            <div className="text-gray-500">{trip.rider_mobile_number}</div>
                          </div>
                        ) : (
                          <div className="text-gray-500">No contact</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getUrgencyLevel(trip.pickup_at).className}>
                        {getUrgencyLevel(trip.pickup_at).level}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        {getStatusBadge(trip.trip_state)}
                        <div className="flex items-center">
                          {getProgressIcons(trip.trip_state)}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-black">
          Showing {filteredTrips.length} of {trips.length} trips
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OperatorTripList;